/**
 * What stands between this project and a reconstruction, before the button.
 *
 * A checklist of measured facts, not a prediction. There is no score, no
 * percentage and no "likely to succeed": the backend counts what exists and
 * reports what it measured, and the person decides. A number implying "78%
 * likely to work" would be invented, and it would be believed.
 *
 * Two kinds of row, and the difference is the whole design:
 *
 * - **Engel** - the create call will refuse. While one stands, the start
 *   button is not offered, because a button that returns 409 is worse than no
 *   button. The backend's `can_start` is exactly "no blockers", so this screen
 *   does not re-derive it and cannot derive it differently.
 * - **Uyarı** - the run will start; the result may be worse and the reader
 *   should know. A warning never hides the button.
 *
 * The sentences are written here, from a code and its counts. The backend
 * sends neither prose nor a percentage - a copy edit in Python would otherwise
 * change what a person is told without anybody reviewing it as copy.
 *
 * Nothing here mentions the machine. Whether *this* API process can see an
 * engine is a hint the tab shows elsewhere; CUDA, GPUs and hardware names are
 * not user-facing claims this product is in a position to make.
 */

import { useNavigate } from "react-router-dom";

import type { PreflightFinding, ReconstructionReadiness } from "@/api/types";
import { Section } from "@/components/product";
import { Button, StatusBadge } from "@/components/ui";
import { paths } from "@/app/paths";

/** Where the reader goes to fix one finding, when a real screen fixes it. */
interface Remedy {
  label: string;
  to: string;
}

/**
 * One row's sentence and its remedy.
 *
 * Composed from the code and the counts the backend measured. A code this
 * build does not know renders as an unnamed finding rather than as nothing:
 * a preflight that silently drops a blocker would show a button the API then
 * refuses.
 */
function describe(
  finding: PreflightFinding,
  projectId: string,
): { text: string; remedy: Remedy | null } {
  const count = finding.count ?? 0;
  const required = finding.required ?? 0;

  switch (finding.code) {
    case "not_enough_input":
      return {
        text: `Rekonstrüksiyon için en az ${required} kare gerekiyor; şu anda ${count} kare kullanılabilir. Bir video, yalnızca üzerinde rekonstrüksiyon amaçlı kare seçimi çalıştırıldıktan sonra sayılır.`,
        remedy: { label: "Çekimlere git", to: paths.projectSessions(projectId) },
      };
    case "job_already_active":
      return {
        // No remedy: the action is to wait, and a button that cannot help
        // would only look like one that should.
        text: "Bu proje için bir rekonstrüksiyon şu anda sırada veya çalışıyor. İkinci bir çalıştırma aynı girdileri ikinci kez dondurur; bitmesini bekleyin.",
        remedy: null,
      };
    case "videos_not_prepared":
      return {
        text: `${count} video için henüz rekonstrüksiyon amaçlı kare seçimi çalıştırılmadı. Bu videoların kareleri yukarıdaki sayıya dahil değil.`,
        remedy: null,
      };
    case "inputs_will_be_sampled":
      return {
        // A fact about *how* it will run, not a problem. Sampling is spread
        // across the whole capture; the first N would be the one wrong answer.
        text: `${count} kare var, bu profilin bütçesi ${required}. Kareler çekimin tamamına yayılarak örneklenecek; baştan kesilmeyecek. İşin kaydı hangi örnekleme yapıldığını saklar.`,
        remedy: null,
      };
    case "quality_issues":
      return {
        text: `${count} dosyada ölçülen kalite sorunu var. Bu dosyalar yine de girdi olarak kullanılır; sonucu zayıflatabilirler.`,
        remedy: { label: "Çekimlere git", to: paths.projectSessions(projectId) },
      };
    case "quality_not_assessed":
      return {
        // An absence, not a fault. Unassessed files are used; nobody has
        // measured them, which is a different sentence from "they are bad".
        text: `${count} dosya için kalite değerlendirmesi çalıştırılmadı. Bu bir sorun tespiti değil; bu dosyalar hakkında ölçülmüş bir bilgi yok.`,
        remedy: { label: "Çekimlere git", to: paths.projectSessions(projectId) },
      };
    case "failed_media":
      return {
        text: `${count} dosyanın yüklemesi veya çözümlemesi başarısız oldu. Bu dosyalar hiçbir zaman girdi olamaz.`,
        remedy: { label: "Çekimlere git", to: paths.projectSessions(projectId) },
      };
    case "previous_run_failed":
      return {
        text: "Bu projedeki son rekonstrüksiyon başarısız oldu. Yeni bir çalıştırma yeni bir sürüm olarak kaydedilir; başarısız olan kayıt silinmez.",
        remedy: null,
      };
    case "head_coverage_not_measured":
      return {
        text: "Bu proje için kapsama analizi henüz çalıştırılmadı. Rekonstrüksiyon buna bağlı değildir; kapsama, çekimin kafanın ölçülebilen bölgelerini ne kadar gördüğünü söyler.",
        remedy: { label: "Kapsamaya git", to: paths.projectCoverage(projectId) },
      };
    case "head_coverage_insufficient":
      return {
        text: "Ölçülen bölgelerde kapsama yeterli değil. Rekonstrüksiyon yine de çalıştırılabilir; kapsama geometriyi engellemez.",
        remedy: { label: "Kapsamaya git", to: paths.projectCoverage(projectId) },
      };
    default:
      // A finding from a newer server. Saying that one exists is honest;
      // dropping it would hide a blocker and leave the button unexplained.
      return { text: "Sunucu, bu sürümün tanımadığı bir bulgu bildirdi.", remedy: null };
  }
}

function FindingRow({
  finding,
  projectId,
}: {
  finding: PreflightFinding;
  projectId: string;
}) {
  const navigate = useNavigate();
  const { text, remedy } = describe(finding, projectId);
  const blocking = finding.severity === "blocker";

  return (
    <li className="preflight__row">
      {/* A badge with a word and a dot, never colour alone: "engel" and
          "uyarı" have to be distinguishable in greyscale. */}
      <StatusBadge tone={blocking ? "danger" : "caution"}>
        {blocking ? "Engel" : "Uyarı"}
      </StatusBadge>
      <p className="preflight__text">{text}</p>
      {remedy && (
        <Button onClick={() => navigate(remedy.to)}>{remedy.label}</Button>
      )}
    </li>
  );
}

export function PreflightPanel({
  readiness,
  projectId,
}: {
  readiness: ReconstructionReadiness;
  projectId: string;
}) {
  const findings = readiness.findings ?? [];

  return (
    <Section
      title="Rekonstrüksiyon ön kontrolü"
      description="Ölçülen durum. Bir başarı tahmini değildir."
    >
      {/*
        The counts this is all about, stated plainly. Frames, not a
        percentage - a percentage of what would be the question, and any
        answer to it would be invented.
      */}
      <div className="preflight__facts">
        <span>
          Kullanılabilir kare: <strong>{readiness.usable_frame_count}</strong>
        </span>
        <span>En az gereken: {readiness.minimum_required}</span>
        <span>Profil bütçesi: {readiness.maximum_allowed}</span>
      </div>

      {findings.length === 0 ? (
        <p className="muted">
          Ölçülen bir engel veya uyarı yok. Bu, sonucun iyi olacağını değil,
          bilinen bir sorun bulunmadığını söyler.
        </p>
      ) : (
        <ul className="preflight__list">
          {findings.map((finding) => (
            <FindingRow
              key={`${finding.code}-${finding.severity}`}
              finding={finding}
              projectId={projectId}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}
